import { APIFactory } from 'ui-toolkit/rest/APIFactory';
import { axiosErrorHandler } from 'ui-toolkit/rest/axiosErrorHandler';
import { IWireRoutingResponse } from '../../../types/order/escrow/IWireRoutingResponse';
import { AxiosResponse } from 'axios';
import { IUnderwriterRemittanceData } from '../../../types/order/escrow/IUnderwriterRemittanceData';
import { convertUnderwriterRemittanceResponseToUnderwriterRemittanceData } from './convertUnderwriterRemittanceResponseToUnderwriterRemittanceData';
import { IEscrowStatementResponse } from '../../../types/order/escrow/ISettlementStatement';
import { IEscrowFileCompleteData } from '../../../types/order/escrow/IEscrowFileCompleteData';
import { baseUrlService } from '../../../ui-toolkit/rest/BaseUrlService';
import {
  convertIEscrowFileCompleteResponseDataToIEscrowFileData,
  IEscrowFileCompleteResponse,
} from '../../../types/order/escrow/response/IEscrowFileCompleteResponse';
import { IWireDepositResponse } from '../../../types/order/escrow/response/IWireDepositResponse';
import { IWireDepositFormData } from '../../../types/order/escrow/state/IWireDepositFormData';
import { ICheckDepositData, ICheckDepositResponse } from '../../../types/order/escrow/ICheckDeposit';
import { IManualCheckDisbursementResponse } from '../../../types/order/escrow/response/IManualCheckDisbursementResponse';
import { IManualCheckDisbursementPostData } from '../../../types/order/escrow/state/manualChecks/IManualCheckDisbursementPostData';
import { IManualCheckDisbursementPutData } from '../../../types/order/escrow/state/manualChecks/IManualCheckDisbursementPutData';
import { convertIInternalTransferDataToIInternalTransferPostData } from '../../../types/order/escrow/state/internalTransfers/IInternalTransferPostData';
import { IInternalTransferResponse } from '../../../types/order/escrow/response/IInternalTransferResponse';
import { IInternalTransferPatchData } from '../../../types/order/escrow/state/internalTransfers/IInternalTransferPatchData';
import {
  convertIAchPushDepositDataToIAchDepositFormData,
  IAchDepositFormData,
} from '../../../types/order/escrow/state/IAchDepositFormData';
import { IAchPullDepositResponse } from '../../../types/order/escrow/response/IAchPullDepositResponse';
import { IAchAccountResponse } from '../../../types/accounting/response/IEscrowExpectedAchPullDepositResponse';
import { IWireDisbursementFormData } from 'types/order/escrow/state/IWireDisbursementFormData';
import { IEscrowWireDisbursementsData } from 'types/order/escrow/IEscrowWireDisbursementsData';
import {
  convertIAchPushFormDataToICreateUpdateACHPushDisbursement,
  IAchPushFormData,
} from '../../../types/order/escrow/IAchPushFormData';
import { IAchPushResponse } from '../../../types/order/escrow/response/IAchPushResponse';
import { IAchAccountData } from '../../../types/accounting/data/IEscrowExpectedIncomingAchData';
import { IEscrowBankAccountResponse } from '../../../types/accounting/response/IEscrowBankAccountResponse';
import { IInternalTransferData } from '../../../types/order/escrow/IInternalTransferData';
import { ISendUnderwriterRemittancesData } from '../../../types/order/escrow/ISendUnderwriterRemittancesData';
import { IPostEscrowCDItemsData } from 'types/order/escrow/response/IPostEscrowCDItemsData';
import { IExpectedAchPullDepositRequestWithId } from '../../../types/order/escrow/request/IExpectedAchPullDepositRequest';
import { IAchPushDisbursementRequestWithId } from '../../../types/order/escrow/request/IAchPushDisbursementRequest';
import { ICreateWireDisbursementRequest } from '../../../types/order/escrow/request/IWireDisbursementRequest';

//simple runtime cache to avoid adding the overhead of axios-cache-adapter. no need to over-engineer for this naive use case
const wireRoutingNumberCache: {
  [routing_number: string]: Promise<AxiosResponse<IWireRoutingResponse>>;
} = {};

export const getWireRoutingByRoutingNumber = async (query: string): Promise<IWireRoutingResponse | undefined> => {
  if (wireRoutingNumberCache[query] === undefined) {
    wireRoutingNumberCache[query] = APIFactory(baseUrlService.ApiLocation).get<IWireRoutingResponse>(
      `/escrow/v1/wire_routing/${query}`
    );
  }
  return new Promise((resolve, reject) => {
    wireRoutingNumberCache[query]
      .then((result) => resolve(result.data))
      .catch(() => reject(new Error('Unable to get bank info for routing number')));
  });
};

export const getUnderwriterRemittancesByOrderId = async (orderId: string): Promise<IUnderwriterRemittanceData[]> =>
  await APIFactory(baseUrlService.ApiLocation)
    .get<IUnderwriterRemittanceData[]>(`/escrow/v1/underwriter_remittances/${orderId}`)
    .then((response) => response.data.map(convertUnderwriterRemittanceResponseToUnderwriterRemittanceData))
    .catch(axiosErrorHandler);

export const getEscrowStatement = async (orderID: string): Promise<IEscrowStatementResponse> => {
  const API = APIFactory(baseUrlService.ApiLocation);
  const result = await API.get<IEscrowStatementResponse>(`/documents/v4/statement_for_escrow/${orderID}`);
  return result.data;
};

export const getEscrowFileCompleteByOrderIdAsync = async (orderId: string): Promise<IEscrowFileCompleteData> =>
  await APIFactory(baseUrlService.ApiLocation)
    .get<IEscrowFileCompleteResponse>(`/timber/api/internal/escrow/files/order/${orderId}`)
    .then((response) => convertIEscrowFileCompleteResponseDataToIEscrowFileData(response.data))
    .catch(axiosErrorHandler);

export const getAchAccountsExcludingAD = async (): Promise<IAchAccountResponse[]> =>
  await APIFactory(baseUrlService.ApiLocation)
    .get<IAchAccountResponse[]>(`/timber/api/internal/escrow/ach_accounts`, {
      data: {
        excludeNames: ['American Digital Title Insurance Company'],
      },
    })
    .then((response) => response.data)
    .catch(axiosErrorHandler);

// Wire Deposit API's == Expected Incoming Wire API's
// CREATE
export interface ICreateWireDepositRequest {
  escrowFileId: string;
  newWireData: IWireDepositFormData[];
}

export const createWireDepositAsync = async ({
  escrowFileId,
  newWireData,
}: ICreateWireDepositRequest): Promise<IWireDepositResponse[]> => {
  const promise = Promise.all(
    newWireData.map((wire) =>
      APIFactory(baseUrlService.ApiLocation).post<IWireDepositResponse>(
        `/timber/api/internal/escrow/${escrowFileId}/expected-incoming-wires`,
        wire
      )
    )
  );
  return promise.then((response) => response.map((res) => res.data)).catch(axiosErrorHandler);
};

// UPDATE
export const updateWireDepositAsync = async (update: IWireDepositFormData[]): Promise<IWireDepositResponse[]> => {
  const promise = Promise.all(
    update.map((wire) =>
      APIFactory(baseUrlService.ApiLocation).put<IWireDepositResponse>(
        `/timber/api/internal/escrow/expected-incoming-wires/${wire.id}`,
        wire
      )
    )
  );
  return promise.then((response) => response.map((res) => res.data)).catch(axiosErrorHandler);
};

// DELETE
export const deleteWireDepositAsync = async (wireDepositId: string): Promise<any> =>
  await APIFactory(baseUrlService.ApiLocation)
    .delete<IWireDepositResponse>(`/timber/api/internal/escrow/expected-incoming-wires/${wireDepositId}`)
    .catch(axiosErrorHandler);

// ACH Deposit API's == Expected ACH Pull API's
// CREATE
export interface ICreateAchDepositRequest {
  escrowFileId: string;
  newAchData: IAchDepositFormData[];
}

export const createAchDepositAsync = async ({
  escrowFileId,
  newAchData,
}: ICreateAchDepositRequest): Promise<IAchPullDepositResponse[]> => {
  const promise = Promise.all(
    newAchData.map((data) =>
      APIFactory(baseUrlService.ApiLocation).post<IAchPullDepositResponse>(
        `/timber/api/internal/escrow/${escrowFileId}/expected-ach-pull-deposits`,
        convertIAchPushDepositDataToIAchDepositFormData(data)
      )
    )
  );
  return promise.then((response) => response.map((res) => res.data)).catch(axiosErrorHandler);
};

// UPDATE
// the ...update has the shape of the API's expected request body, ExpectedAchPullDepositRequest
export const updateAchDepositAsync = async (
  updates: IExpectedAchPullDepositRequestWithId[]
): Promise<IAchPullDepositResponse[]> => {
  const updatePromise = Promise.all(
    updates.map(({ id, ...update }) => {
      return APIFactory(baseUrlService.ApiLocation).put<IAchPullDepositResponse>(
        `/timber/api/internal/escrow/expected-ach-pull-deposits/${id}`,
        { ...update }
      );
    })
  );
  return updatePromise.then((response) => response.map((res) => res.data)).catch(axiosErrorHandler);
};

// DELETE
export const deleteAchDepositAsync = async (achDepositId: string): Promise<any> =>
  await APIFactory(baseUrlService.ApiLocation).delete<IAchPullDepositResponse>(
    `/timber/api/internal/escrow/expected-ach-pull-deposits/${achDepositId}`
  );

// SEND
export const sendAchDepositAsync = async (achDepositId: string): Promise<any> =>
  await APIFactory(baseUrlService.ApiLocation).patch<IAchPullDepositResponse>(
    `/timber/api/internal/escrow/expected-ach-pull-deposits/${achDepositId}`
  );

// Check Deposit API's == Expected Incoming Check API's
// CREATE
export interface ICreateCheckDepositRequest {
  escrowFileId: string;
  newCheckData: ICheckDepositData[];
}

export const createCheckDepositAsync = async ({
  escrowFileId,
  newCheckData,
}: ICreateCheckDepositRequest): Promise<ICheckDepositResponse[]> => {
  const createPromise = Promise.all(
    newCheckData.map((check) =>
      APIFactory(baseUrlService.ApiLocation).post<ICheckDepositResponse>(
        `/timber/api/internal/escrow/${escrowFileId}/expected-incoming-checks`,
        check
      )
    )
  );
  return createPromise.then((response) => response.map((res) => res.data)).catch(axiosErrorHandler);
};

// UPDATE
export const updateCheckDepositAsync = async (updates: ICheckDepositData[]): Promise<ICheckDepositResponse[]> => {
  const updatePromise = Promise.all(
    updates.map((update) =>
      APIFactory(baseUrlService.ApiLocation).put<ICheckDepositResponse>(
        `/timber/api/internal/escrow/expected-incoming-checks/${update.id}`,
        update
      )
    )
  );
  return updatePromise.then((response) => response.map((res) => res.data)).catch(axiosErrorHandler);
};

// DELETE
export const deleteCheckDepositAsync = async (checkDepositId: string): Promise<any> =>
  await APIFactory(baseUrlService.ApiLocation)
    .delete<ICheckDepositResponse>(`/timber/api/internal/escrow/expected-incoming-checks/${checkDepositId}`)
    .catch(axiosErrorHandler);

//ACH Pushes API's
//CREATE
export interface ICreateAchPushRequest {
  escrowFileId: string;
  data: IAchPushFormData[];
}

export const createAchPushAsync = async ({
  escrowFileId,
  data,
}: ICreateAchPushRequest): Promise<IAchPushResponse[]> => {
  const promise = Promise.all(
    data.map((ach) =>
      APIFactory(baseUrlService.ApiLocation).post<IAchPushResponse>(
        `/timber/api/internal/escrow/${escrowFileId}/ach_push_disbursements`,
        convertIAchPushFormDataToICreateUpdateACHPushDisbursement(ach)
      )
    )
  );
  return promise.then((response) => response.map((res) => res.data)).catch(axiosErrorHandler);
};

//UPDATE
// the ...update has the shape of the API's expected request body, AchPushDisbursementRequest
export const updateAchPushAsync = async (updates: IAchPushDisbursementRequestWithId[]): Promise<IAchPushResponse[]> => {
  const promise = Promise.all(
    updates.map(({ id, ...update }) =>
      APIFactory(baseUrlService.ApiLocation).put<IAchPushResponse>(
        `/timber/api/internal/escrow/ach_push_disbursements/${id}`,
        { ...update }
      )
    )
  );
  return promise.then((response) => response.map((res) => res.data)).catch(axiosErrorHandler);
};

//DELETE
export const deleteAchPushAsync = async (achPushId: string): Promise<any> =>
  await APIFactory(baseUrlService.ApiLocation).delete<IAchPushResponse>(
    `/timber/api/internal/escrow/ach_push_disbursements/${achPushId}`
  );

// SEND
export const sendAchPushAsync = async ({
  escrowFileId,
  achPushData,
}: {
  escrowFileId: string;
  achPushData: any;
}): Promise<any> =>
  await APIFactory(baseUrlService.ApiLocation).patch(
    `/timber/api/internal/escrow/ach_push_disbursements/${achPushData.id}/send`
  );

//GET ACH ACCOUNTS
export const getAchAccountsAsync = async (): Promise<IAchAccountData[]> =>
  await APIFactory(baseUrlService.ApiLocation)
    .get<IAchAccountResponse[]>(`/timber/api/internal/escrow/ach_accounts`)
    .then((response) => response.data)
    .catch(axiosErrorHandler);

// Manual Check Disbursement API's
// CREATE
export interface ICreateManualCheckRequest {
  escrowFileId: string;
  newCheckData: IManualCheckDisbursementPostData[];
}

export const createManualCheckAsync = async ({
  escrowFileId,
  newCheckData,
}: ICreateManualCheckRequest): Promise<IManualCheckDisbursementResponse[]> => {
  const promise = Promise.all(
    newCheckData.map((check) =>
      APIFactory(baseUrlService.ApiLocation).post<IManualCheckDisbursementResponse>(
        `/timber/api/internal/escrow/${escrowFileId}/escrow_manual_check_disbursements`,
        check
      )
    )
  );
  return promise.then((response) => response.map((res) => res.data)).catch(axiosErrorHandler);
};

// UPDATE
export const updateManualCheckAsync = async (
  update: IManualCheckDisbursementPutData[]
): Promise<IManualCheckDisbursementResponse[]> => {
  const promise = Promise.all(
    update.map((check) =>
      APIFactory(baseUrlService.ApiLocation).put<IManualCheckDisbursementResponse>(
        `/timber/api/internal/escrow/escrow_manual_check_disbursements/${check.id}`,
        check
      )
    )
  );
  return promise.then((response) => response.map((res) => res.data)).catch(axiosErrorHandler);
};

// DELETE
export const deleteManualCheckAsync = async (manualCheckId: string): Promise<any> =>
  await APIFactory(baseUrlService.ApiLocation)
    .delete<ICheckDepositResponse>(`/timber/api/internal/escrow/escrow_manual_check_disbursements/${manualCheckId}`)
    .catch(axiosErrorHandler);

// Internal Transfer API's
// CREATE
export interface ICreateInternalTransferRequest {
  escrowFileId: string;
  depositEscrowBankAccountId: string;
  newTransferData: IInternalTransferData[];
}

export const createInternalTransferAsync = async ({
  escrowFileId,
  depositEscrowBankAccountId,
  newTransferData,
}: ICreateInternalTransferRequest): Promise<IInternalTransferResponse[]> => {
  const promise = Promise.all(
    newTransferData.map((transfer) => {
      const postData = convertIInternalTransferDataToIInternalTransferPostData(
        escrowFileId,
        transfer,
        depositEscrowBankAccountId
      );
      return APIFactory(baseUrlService.ApiLocation).post<IInternalTransferResponse>(
        `/timber/api/internal/escrow/expected_internal_transfers`,
        postData
      );
    })
  );
  return promise.then((response) => response.map((res) => res.data)).catch(axiosErrorHandler);
};

// UPDATE
export const updateInternalTransferAsync = async (
  updates: IInternalTransferPatchData[]
): Promise<IInternalTransferResponse[]> => {
  const promise = Promise.all(
    updates.map((update) =>
      APIFactory(baseUrlService.ApiLocation).patch<IInternalTransferResponse>(
        `/timber/api/internal/escrow/expected_internal_transfers/${update.id}`,
        update
      )
    )
  );
  return promise.then((response) => response.map((response) => response.data)).catch(axiosErrorHandler);
};

// SEND
export const sendInternalTransferAsync = async (
  update: IInternalTransferPatchData
): Promise<IInternalTransferResponse> =>
  APIFactory(baseUrlService.ApiLocation)
    .patch<IInternalTransferResponse>(`/timber/api/internal/escrow/expected_internal_transfers/${update.id}/send`)
    .then((response) => response.data)
    .catch(axiosErrorHandler);

// GET
export interface IGetBankAccountsByTransactionCategoryAndStateRequest {
  category: string;
  usState: string;
  rowIndex: string;
}

//This is used for all states EXCEPT AZ, NV, TX, WA
export const getBankAccountForCategoryAndState = async (
  request: IGetBankAccountsByTransactionCategoryAndStateRequest
): Promise<AxiosResponse<IEscrowBankAccountResponse>> =>
  await APIFactory(baseUrlService.ApiLocation)
    .get<IEscrowBankAccountResponse>(
      `/timber/api/internal/escrow/bank_accounts/findBy/transactionCategory/${request.category}/state/${request.usState}`
    )
    .catch(axiosErrorHandler);

//This is used for AZ, NV, TX, WA
export const getDestinationAccountsForState = async (orderId: string): Promise<IEscrowBankAccountResponse[]> =>
  await APIFactory(baseUrlService.ApiLocation)
    .get<IEscrowBankAccountResponse[]>(`/timber/api/internal/escrow/bank_accounts/${orderId}/deposit_accounts`)
    .catch(axiosErrorHandler)
    .then((response) => response.data);

// DELETE
export const deleteInternalTransferAsync = async (checkDepositId: string): Promise<any> =>
  await APIFactory(baseUrlService.ApiLocation)
    .delete<IInternalTransferResponse>(`/timber/api/internal/escrow/expected_internal_transfers/${checkDepositId}`)
    .catch(axiosErrorHandler);

// Wire Disbursements API's
// CREATE
export interface IEscrowWireDisbursementsRequest {
  escrowFileId: string;
  newDisbursementData: ICreateWireDisbursementRequest[];
}

export const postWireDisbursementAsync = async ({
  escrowFileId,
  newDisbursementData,
}: IEscrowWireDisbursementsRequest): Promise<IEscrowWireDisbursementsData[]> => {
  const promise = Promise.all(
    newDisbursementData.map((wire) =>
      APIFactory(baseUrlService.ApiLocation).post<IEscrowWireDisbursementsData>(
        `/timber/api/internal/escrow/${escrowFileId}/wire_disbursements`,
        wire
      )
    )
  );
  return promise.then((response) => response.map((res) => res.data)).catch(axiosErrorHandler);
};

// UPDATE
// the ...update has the shape of the API's expected request body, IWireDisbursementRequest
export const updateWireDisbursementAsync = async (
  updates: IWireDisbursementFormData[]
): Promise<IEscrowWireDisbursementsData[]> => {
  const promise = Promise.all(
    updates.map(({ id, ...update }) =>
      APIFactory(baseUrlService.ApiLocation).put<IEscrowWireDisbursementsData>(
        `/timber/api/internal/escrow/wire_disbursements/${id}`,
        { ...update }
      )
    )
  );
  return promise.then((response) => response.map((res) => res.data)).catch(axiosErrorHandler);
};

// DELETE
export const deleteWireDisbursementAsync = async (wireDisbursementId: string): Promise<any> =>
  await APIFactory(baseUrlService.ApiLocation)
    .delete<IEscrowWireDisbursementsData>(`/timber/api/internal/escrow/wire_disbursements/${wireDisbursementId}`)
    .catch(axiosErrorHandler);

// SEND
export const sendWireDisbursementAsync = async (data: IWireDisbursementFormData): Promise<any> =>
  await APIFactory(baseUrlService.ApiLocation)
    .patch<IEscrowWireDisbursementsData>(`/timber/api/internal/escrow/wire_disbursements/${data.id}/send`)
    .catch(axiosErrorHandler);

export interface ISendUnderwriterRemittanceRequest {
  escrowFileId: string;
  remittanceData: ISendUnderwriterRemittancesData;
}

export const sendUnderwriterRemittancesByEscrowFileIdAsync = async ({
  escrowFileId,
  remittanceData,
}: ISendUnderwriterRemittanceRequest): Promise<IUnderwriterRemittanceData[]> =>
  await APIFactory(baseUrlService.ApiLocation)
    .post(`/escrow/v1/underwriter_remittance_pushes/${escrowFileId}`, remittanceData)
    .then((response) => response.data)
    .catch(axiosErrorHandler);
//POST CD Item

export const createCDItemsAsync = async (cdItems: IPostEscrowCDItemsData[]): Promise<any> => {
  const promise = Promise.all(
    cdItems.map((item) =>
      APIFactory(baseUrlService.ApiLocation).post<IEscrowWireDisbursementsData>(
        `/timber/api/internal/escrow/cd_items`,
        item
      )
    )
  );
  return promise.then((response) => response.map((res) => res.data)).catch(axiosErrorHandler);
};

export const setFundedAtAsync = async (orderId: string): Promise<any> =>
  await APIFactory(baseUrlService.ApiLocation)
    .post(`/timber/api/internal/orders/${orderId}/set_funded_at`)
    .then((response) => response.data)
    .catch(axiosErrorHandler);
